import api from '../../../services/api';
import * as purchase from './purchaseInvoiceImportApi';
import * as sales from './salesInvoiceImportApi';
import { normalizePurchaseInvoiceDraft, normalizeSalesInvoiceDraft } from '../../../dto/workflowInvoice';
import { buildPurchaseInvoicePayload, buildSalesInvoicePayload } from '../workflowInvoicePayload';
import type { WorkflowInvoice, WorkflowInvoiceDirection, WorkflowInvoiceLineDimensions } from '../../../types/workflowInvoice';
import { normalizeInvoiceCorrectionState, type InvoiceCorrectionState } from '../../../dto/invoiceCorrection';

export interface InvoiceApprovalInput {
  purchaseAlignmentFingerprint?: string;
  salesAlignmentFingerprint?: string;
  expectedVersion: number;
  idempotencyKey: string;
  /** Absent: approval uses the invoice issue-date month, a receipt the invoice's own recognition month (2026-10-13). */
  recognitionMonth?: string;
  pzNumbers?: Array<{ supplierOrderId: string; pzNumber: string }>;
  receiptCosts?: Array<{ purchaseInvoiceItemId: string; totalCostPLN: number; reason?: string }>;
  pendingStockSelections?: Array<{ purchaseInvoiceItemId: string; pendingIssueIds: string[]; reason?: string }>;
  /** Audited operational-exception acknowledgement (see operationalExceptionFlow). */
  confirmOperationalException?: boolean;
  operationalExceptionFingerprint?: string;
  operationalExceptionReason?: string;
}
/** Advance / settlement invoices of one ZK and their balance against its sale value (net PLN). */
export interface AdvanceInvoiceBalance {
  documents: Array<{ id: string; number: string | null; documentKind: 'ADVANCE' | 'SETTLEMENT'; recognitionMonth: string | null; netPLN: number; pending?: boolean }>;
  advancesNetPLN: number;
  settlementsNetPLN: number;
  invoicedNetPLN: number;
  orderNetPLN: number | null;
  /** Net PLN of the active ordinary invoices of the same ZK (they settle their own lines). */
  ordinaryNetPLN: number;
  balancePLN: number | null;
  hasSettlement: boolean;
  warning: { code: 'ADVANCE_BALANCE_MISMATCH'; balancePLN: number; message: string } | null;
}
export interface InvoiceApprovalResult {
  invoice: WorkflowInvoice;
  orders: Array<{ id: string; commerciallyFrozen: boolean; coverageState?: 'PARTIAL' | 'FULL' | null }>;
  advanceBalance?: AdvanceInvoiceBalance | null;
  /** WP2: approval of a correction invoice (net before/after/difference in PLN). */
  correction?: InvoiceCorrectionSummary | null;
  settlement?: unknown;
  replayed: boolean;
}
export function invoiceWorkflowApi(direction: WorkflowInvoiceDirection) {
  const isPurchase = direction === 'PURCHASE';
  return {
    create: isPurchase ? purchase.createPurchaseInvoiceDraft : sales.createSalesInvoiceDraft,
    fetch: isPurchase ? purchase.fetchPurchaseInvoiceDraft : sales.fetchSalesInvoiceDraft,
    update: isPurchase ? purchase.updatePurchaseInvoiceDraft : sales.updateSalesInvoiceDraft,
    remove: isPurchase ? purchase.deletePurchaseInvoiceDraft : sales.deleteSalesInvoiceDraft,
    attach: isPurchase ? purchase.attachPurchaseInvoiceSourceFile : sales.attachSalesInvoiceSourceFile,
    download: isPurchase ? purchase.downloadPurchaseInvoiceSourceFile : sales.downloadSalesInvoiceSourceFile,
    extract: isPurchase ? purchase.requestPurchaseInvoiceExtraction : sales.requestSalesInvoiceExtraction,
    apply: isPurchase ? purchase.applyPurchaseInvoiceExtraction : sales.applySalesInvoiceExtraction,
    payload: isPurchase ? buildPurchaseInvoicePayload : buildSalesInvoicePayload,
    /** WP2: a CORRECTION draft copied from the approved original (lines "after correction"). */
    async createCorrection(originalId: string): Promise<WorkflowInvoice> {
      const path = isPurchase ? '/purchase-invoices' : '/invoices/import-drafts';
      const response = await api.post(`${path}/${originalId}/corrections`);
      return (isPurchase ? normalizePurchaseInvoiceDraft : normalizeSalesInvoiceDraft)(response.data);
    },
    async approve(id: string, input: InvoiceApprovalInput): Promise<InvoiceApprovalResult> {
      const path = isPurchase ? '/purchase-invoices' : '/invoices/import-drafts';
      const response = await api.post(`${path}/${id}/approve`, input);
      const normalize = isPurchase ? normalizePurchaseInvoiceDraft : normalizeSalesInvoiceDraft;
      return { ...response.data, invoice: normalize(response.data.invoice) };
    },
  };
}

export interface InvoiceCorrectionSummary {
  originalInvoiceId: string;
  originalNumber: string | null;
  netBeforePLN: number;
  netAfterPLN: number;
  netDeltaPLN: number;
  /** Sales: the „Transport (refaktura)” part of the net before/after (PLN); present only when either has one. */
  transportReinvoiceBeforePLN?: number | null;
  transportReinvoiceAfterPLN?: number | null;
  /** WP15: the validated complaint of a „Reklamacja” correction. */
  complaint?: {
    reason: 'COMPLAINT';
    lines: Array<{ sourceLineId: string; name: string; unit: string; quantity: number; creditPLN: number }>;
  } | null;
}

const invoicePath = (direction: WorkflowInvoiceDirection) => (direction === 'PURCHASE' ? '/purchase-invoices' : '/invoices/import-drafts');

/** WP2: corrections, withdrawal and lines added after the invoice. */
export async function fetchInvoiceCorrectionState(direction: WorkflowInvoiceDirection, id: string): Promise<InvoiceCorrectionState> {
  const { data } = await api.get(`${invoicePath(direction)}/${id}/corrections`);
  return normalizeInvoiceCorrectionState(data);
}

/**
 * WP15: reason „Reklamacja” and the complained quantity per ZK/ZD line of a
 * correction draft (`reason: null` clears the complaint). Returns the draft.
 */
export async function saveInvoiceComplaint(
  direction: WorkflowInvoiceDirection,
  id: string,
  input: { expectedVersion: number; reason: 'COMPLAINT' | null; lines: Array<{ sourceLineId: string; quantity: number }> },
): Promise<WorkflowInvoice> {
  const { data } = await api.put(`${invoicePath(direction)}/${id}/complaint`, input);
  return (direction === 'PURCHASE' ? normalizePurchaseInvoiceDraft : normalizeSalesInvoiceDraft)(data);
}

/** WP2: withdraw an approved invoice (ADMIN only; the backend enforces it). */
export async function withdrawInvoice(direction: WorkflowInvoiceDirection, id: string, input: { reason: string; recognitionMonth: string }): Promise<{ order: { id: string; status: string } }> {
  return (await api.post(`${invoicePath(direction)}/${id}/withdraw`, input)).data;
}

export interface InvoiceApprovalPreview {
  correction?: InvoiceCorrectionSummary | null;
  purchaseAlignment?: { fingerprint: string; changes: Array<{ sourceLineId: string | null; productName: string | null; beforeCurrency?: string; afterCurrency?: string; field: string; before: string | number | null; after: string | number | null }> } | null;
  salesAlignment?: { fingerprint: string; changes: Array<{ sourceLineId: string | null; productName: string | null; beforeCurrency?: string; afterCurrency?: string; field: string; before: string | number | null; after: string | number | null }> } | null;
  financialOnly?: boolean;
  approvalIssue?: { code: string; message: string } | null;
  /** Other non-withdrawn documents with this number (drafts included): a warning, never a block. */
  sameNumberDocuments?: Array<{ id: string; status: string; documentKind: string; orderId: string | null; orderNumber: string | null }>;
  advanceBalance?: AdvanceInvoiceBalance | null;
  orders: Array<{ id: string; orderNumber?: string; coverageState: 'PARTIAL' | 'FULL' | null; commerciallyFrozen: boolean; lines: Array<{ sourceLineId: string; productName?: string; orderedQuantity: number | null; acceptedQuantity: number | null; remainingQuantity: number | null; unit: string }> }>;
  pendingStockCandidates: Array<{ purchaseInvoiceItemId: string; candidates: Array<{ id: string; wzDocumentId: string; wzNumber?: string; orderId: string; orderNumber?: string; productName?: string; quantity: number; unit: string; sourceSupplierOrderItemId: string | null }> }>;
}
export async function fetchInvoiceApprovalPreview(direction: WorkflowInvoiceDirection, id: string): Promise<InvoiceApprovalPreview> {
  const path = direction === 'PURCHASE' ? '/purchase-invoices' : '/invoices/import-drafts';
  const { data } = await api.get(`${path}/${id}/approval-preview`);
  if (!data || !Array.isArray(data.orders) || !Array.isArray(data.pendingStockCandidates)
    || data.orders.some((order: unknown) => !order || !Array.isArray((order as { lines?: unknown }).lines))
    || data.pendingStockCandidates.some((group: unknown) => !group || !Array.isArray((group as { candidates?: unknown }).candidates))) {
    throw new Error('Niepełny podgląd pokrycia faktury.');
  }
  return data;
}

export interface PendingReceipt {
  id: string; pzNumber: string; updatedAt: string; receiptDate: string;
  items: Array<{ id: string; productVariantId: string; productName: string; unit: string; quantity: number } & Partial<WorkflowInvoiceLineDimensions>>;
}
/** `productDimensions`: display only (source ZD line / catalog), shown under the product name. */
export interface PurchaseReceiptLine { purchaseInvoiceItemId: string; productName: string; unit: string; remainingQuantity: number; sourceSupplierOrderItemId?: string | null; productDimensions?: WorkflowInvoiceLineDimensions | null }
/** 2026-10-10: the ZD has no inbound truck yet — freight reaches the batch only once one is saved. */
export interface PurchaseReceiptFreightWarning { code: 'SUPPLIER_ORDER_WITHOUT_TRUCK'; supplierOrderId: string }
/** Owner 2026-10-12: the ZD's inbound trucks — which one brought this receipt decides its freight. */
export interface ReceiptTruckChoiceGroup {
  supplierOrderId: string;
  supplierOrderLabel: string;
  trucks: Array<{ id: string; label: string; carriedQuantity: number | null; unit: string | null; freightPLN: number | null; receivedQuantity: number }>;
}
export async function fetchPurchaseReceiptPreview(id: string): Promise<{ version: number; pendingPZs: PendingReceipt[]; lines: PurchaseReceiptLine[]; pzDocuments: Array<{ id: string; pzNumber: string }>; freightWarnings?: PurchaseReceiptFreightWarning[]; truckChoices?: ReceiptTruckChoiceGroup[] }> {
  return (await api.get(`/purchase-invoices/${id}/receipt-preview`)).data;
}
export async function receivePurchaseInvoice(id: string, input: InvoiceApprovalInput & { confirmNewDelivery?: boolean; receiptQuantities: Array<{ purchaseInvoiceItemId: string; quantity: number }>; sourceTransportIds?: string[] }): Promise<{ pzDocuments: Array<{ id: string; pzNumber: string }> }> {
  return (await api.post(`/purchase-invoices/${id}/receive`, input)).data;
}

export async function linkPurchaseReceipt(id: string, input: InvoiceApprovalInput & {
  pzDocumentId: string; expectedPzUpdatedAt: string;
  mapping: Array<{ pzItemId: string; purchaseInvoiceItemId: string }>;
}): Promise<{ pzDocuments: Array<{ id: string; pzNumber: string }> }> {
  return (await api.post(`/purchase-invoices/${id}/link-receipt`, input)).data;
}
